// src/components/Admin/CommentManager/CommentsLogTable.jsx
import { useEffect, useState, useMemo } from 'react';
import {
    Box, TextField, Select, MenuItem, Typography,
    Table, TableHead, TableRow, TableCell, TableBody,
    TableContainer, Paper, CircularProgress, Pagination,
} from '@mui/material';
import api from '../../../api/axios';

const actionLabel = {
    soft_delete: '삭제',
    blind: '블라인드',
    unblind: '블라인드 해제',
    edit: '수정',
};

const ITEMS_PER_PAGE = 15;

export default function CommentsLogTable() {
    const [logs, setLogs] = useState([]);
    const [search, setSearch] = useState('');
    const [actionFilter, setActionFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);

    /* ───────── 데이터 로드 ───────── */
    useEffect(() => {
        api.get('/api/comments/logs/')
            .then((res) => setLogs(res.data.logs || []))
            .catch(() => alert('댓글 로그를 불러오는 데 실패했습니다.'))
            .finally(() => setLoading(false));
    }, []);

    /* ───────── 필터링 ───────── */
    const filtered = useMemo(() => (
        logs
            .filter((log) => (actionFilter === 'all' ? true : log.action === actionFilter))
            .filter((log) =>
                (log.comment_content || '').toLowerCase().includes(search.toLowerCase())
                || (log.post_title || '').toLowerCase().includes(search.toLowerCase())
                || (log.action || '').toLowerCase().includes(search.toLowerCase())
                || (log.memo || '').toLowerCase().includes(search.toLowerCase())
                || (log.nickname || '').toLowerCase().includes(search.toLowerCase()))
    ), [logs, search, actionFilter]);

    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE) || 1;
    const paginated = filtered.slice(
        (page - 1) * ITEMS_PER_PAGE,
        page * ITEMS_PER_PAGE,
    );

    useEffect(() => setPage(1), [search, actionFilter]);

    /* ───────── 로딩 ───────── */
    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    /* ───────── 렌더 ───────── */
    return (
        <Box
            sx={{
                /* 모바일: 전체 90 % 축소 */
                '@media (max-width:600px)': {
                    transform: 'scale(0.9)',
                    transformOrigin: 'top left',
                },
            }}
        >
            {/* 검색·필터 바 */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                    flexWrap: 'wrap',
                }}
            >
                <TextField
                    label="댓글/게시글/작성자/사유"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 240 }}
                />
                <Select
                    size="small"
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                    sx={{ minWidth: 140 }}
                >
                    <MenuItem value="all">전체</MenuItem>
                    <MenuItem value="soft_delete">삭제</MenuItem>
                    <MenuItem value="blind">블라인드</MenuItem>
                    <MenuItem value="unblind">블라인드 해제</MenuItem>
                </Select>
                <Typography variant="body2" color="text.secondary">
                    총&nbsp;
                    {logs.length}
                    개 / 현재&nbsp;
                    {filtered.length}
                    개
                </Typography>
            </Box>

            {/* 테이블 */}
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    '& tr:hover': { bgcolor: 'grey.100' },
                    '& td, & th': {
                        borderBottom: '1px solid #eee',
                        p: 1.25,
                        whiteSpace: 'nowrap',
                        '@media (max-width:600px)': {
                            p: 0.75,
                            fontSize: '0.8rem',
                        },
                    },
                    '& th': { bgcolor: 'grey.50', fontWeight: 'bold' },
                    /* 모바일: 가로 스크롤 허용 */
                    '@media (max-width:600px)': {
                        width: 'max-content',
                        overflowX: 'auto',
                    },
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">ID</TableCell>
                            <TableCell align="left">댓글</TableCell>
                            <TableCell align="left">게시글</TableCell>
                            <TableCell align="left">작성자</TableCell>
                            <TableCell align="center">행동</TableCell>
                            <TableCell align="center">처리자</TableCell>
                            <TableCell align="center">시간</TableCell>
                            <TableCell align="center">사유</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginated.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                                    🔍 조건에 맞는 로그가 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginated.map((log) => (
                                <TableRow key={log.id}>
                                    <TableCell align="center">{log.id}</TableCell>
                                    <TableCell align="left">
                                        {log.comment_content.length > 40
                                            ? `${log.comment_content.slice(0, 40)}...`
                                            : log.comment_content}
                                    </TableCell>
                                    <TableCell align="left">
                                        <a
                                            href={`/main/posts/${log.post_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ textDecoration: 'none', color: '#1976d2' }}
                                        >
                                            {log.post_title}
                                            {' '}
                                            (#
                                            {log.post_id}
                                            )
                                        </a>
                                    </TableCell>
                                    <TableCell align="left">{log.nickname || '-'}</TableCell>
                                    <TableCell align="center">
                                        {actionLabel[log.action] || log.action}
                                    </TableCell>
                                    <TableCell align="center">
                                        {log.acted_by?.trim() || '알 수 없음'}
                                    </TableCell>
                                    <TableCell align="center">{log.acted_at}</TableCell>
                                    <TableCell align="center">{log.memo || '-'}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
                <Box
                    sx={{
                        mt: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        '@media (max-width:600px)': { transform: 'scale(0.9)' },
                    }}
                >
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, v) => setPage(v)}
                        color="primary"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}
        </Box>
    );
}
